import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useToolbar, type ToolbarModule } from '../api/navigation.ts';
import { subTabs } from '../toolbarTabs.ts';

/**
 * The agency level (docs/navigation-brief.md, section 3): a row of modules,
 * each with a count of what is waiting for THIS reader. A case manager's Visits
 * count is their own visits due; tiers 1-2 count the agency's. The server
 * decides which modules a reader has and counts as the reader, so a module
 * the role cannot use is never drawn, and no count reaches past what the
 * reader can see (api/src/routes/navigation.ts).
 *
 * The client level is the sidebar inside a client's record; this row is not a
 * copy of it.
 */

/** Which paths belong to which module, for the active marker. */
const OWNS: Record<string, (path: string) => boolean> = {
  home: (p) => p === '/home' || p === '/lookup',
  clients: (p) =>
    p === '/clients' || p === '/clients/new' || p === '/clients/held' || p === '/clients/discharge-confirmation',
  visits: (p) => p.startsWith('/visits') || (p.startsWith('/facilities') && p !== '/facilities/needing-type'),
  legal: (p) => p.startsWith('/legal'),
  financials: (p) => p.startsWith('/finance'),
  compensation: (p) => p.startsWith('/compensation'),
  reports: (p) => p.startsWith('/reports'),
  tasks: (p) => p.startsWith('/tasks'),
  documents: (p) => p.startsWith('/documents'),
  real_estate: (p) => p.startsWith('/real-estate'),
};

function isActive(m: ToolbarModule, path: string, landing: string): boolean {
  if (m.key === 'home') {
    // Home is the reader's landing; it is marked there only when no other module owns the page.
    const other = Object.entries(OWNS).some(([k, owns]) => k !== 'home' && owns(path));
    return OWNS.home!(path) || (path === landing && !other);
  }
  return OWNS[m.key]?.(path) ?? false;
}

export function ModuleTabs() {
  const toolbar = useToolbar();
  const { pathname } = useLocation();
  if (!toolbar.data) return null;
  const module = toolbar.data.modules.find((m) => m.key !== 'home' && OWNS[m.key]?.(pathname));
  if (!module) return null;
  const tabs = subTabs(module.key, toolbar.data);
  if (tabs.length < 2) return null;
  return (
    <nav aria-label={`${module.label} pages`} className="border-b border-line bg-tint">
      <ul className="no-scrollbar mx-auto flex max-w-[96rem] gap-1 overflow-x-auto px-2 lg:px-4">
        {tabs.map((t) => {
          const active = t.exact ? pathname === t.to : pathname.startsWith(t.to);
          return (
            <li key={t.to} className="shrink-0">
              <Link to={t.to} aria-current={active ? 'page' : undefined}
                    className={`flex min-h-[44px] items-center px-3 text-sm font-semibold ${
                      active ? 'text-neutral-900 underline decoration-accent decoration-[3px] underline-offset-[8px]' : 'text-neutral-700 hover:text-neutral-900'
                    }`}>
                {t.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export function Toolbar() {
  const toolbar = useToolbar();
  const { pathname } = useLocation();
  if (!toolbar.data) {
    // The row keeps its height while it loads, so the page under it does not jump.
    return <div className="h-[52px] bg-brand" aria-hidden="true" />;
  }
  const { modules, landing } = toolbar.data;

  return (
    <nav aria-label="Modules" className="bg-brand">
      <ul className="no-scrollbar mx-auto flex max-w-[96rem] gap-0.5 overflow-x-auto px-2 lg:px-4">
        {modules.map((m) => {
          const active = isActive(m, pathname, landing);
          return (
            <li key={m.key} className="shrink-0">
              <Link
                to={m.path}
                aria-current={active ? 'page' : undefined}
                // Text on the brand is on-brand throughout (6.4 : 1); the module open is underlined in it.
                className={`flex min-h-[52px] items-center gap-1.5 border-b-[3px] px-2 text-[0.9375rem] text-on-brand lg:gap-2 lg:px-3 ${
                  active ? 'border-on-brand font-semibold' : 'border-transparent font-medium hover:border-on-brand/40'
                }`}
              >
                {m.label}
                {m.reserved ? (
                  <span className="rounded border border-dashed border-on-brand/60 px-1.5 text-xs font-medium text-on-brand">
                    Coming
                  </span>
                ) : m.count !== null ? (
                  <span
                    aria-label={`${m.count} waiting`}
                    // A light translucent pill on the brand, as in the approved mockup. 15% white,
                    // not the mockup's 22%: at 22% white text drops to 3.9 : 1; at 15% it is 4.7 : 1.
                    className="min-w-[1.75rem] rounded-full bg-on-brand/15 px-2 text-center text-sm font-semibold tabular-nums text-on-brand"
                  >
                    {m.count.toLocaleString()}
                  </span>
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * The top-right menu: the agency overview and the admin pages, each only where
 * the reader's roles allow (the server's list). Nothing at all when there is
 * nothing in it.
 */
export function TopMenu() {
  const toolbar = useToolbar();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname, location.search]);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const menu = toolbar.data?.menu ?? [];
  if (menu.length === 0) return null;

  return (
    <div ref={box} className="relative">
      <button
        ref={button}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        // On the brand bar (15 October): white on the brand, outlined, never a light fill.
        className="inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-lg border border-on-brand/60
                   px-3 text-[0.9375rem] font-semibold text-on-brand hover:bg-on-brand/10"
      >
        Agency
        <svg viewBox="0 0 20 20" aria-hidden="true" className="size-4 text-on-brand">
          <path d="M5.5 7.5 10 12l4.5-4.5" stroke="currentColor" strokeWidth="1.8" fill="none" />
        </svg>
      </button>
      {open && (
        <div role="menu"
             className="absolute top-full right-0 z-30 mt-1 w-72 overflow-hidden rounded-lg border border-neutral-200
                        bg-white py-1 shadow-lg">
          {menu.map((item, i) => (
            <div key={item.key}>
              {/* The overview first, the administration below it. */}
              {i === 1 && menu[0]!.key === 'agency' && <div className="my-1 border-t border-neutral-200" />}
              <Link role="menuitem" to={item.path}
                    className="flex min-h-[44px] items-center px-4 text-[0.9375rem] font-medium text-neutral-800
                               hover:bg-neutral-50 focus:bg-neutral-100 focus:outline-none">
                {item.label}
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
